import type { Media, SoundEffect } from "@mw/shared/payload-types";

import { env } from "@/env";
import { CACHE_TAG_REVALIDATE } from "@/app/_api/cacheTagRegistry";
import { fetchWithCache } from "@/app/_api/fetchWithCache";
import { logger } from "@utilities/logger";
import { DEFAULT_SOUND_URLS } from "@ui/components/demos/SoundBoard/data";

// Types matching the Python dataclasses
type SoundType = "custom" | "default";

interface CreateSoundRequest {
  default_sound_id?: string;
  history_item_id?: string;
  name: string;
  type: SoundType;
}

interface UpdateSoundRequest extends CreateSoundRequest {
  id?: string;
}

export interface CreateSoundboardRequest {
  icon_id: string;
  name: string;
  sounds: CreateSoundRequest[];
}

export interface UpdateSoundboardRequest extends CreateSoundboardRequest {
  public: boolean;
  sounds: UpdateSoundRequest[];
}

interface SoundResponse extends Omit<CreateSoundRequest, "history_item_id"> {
  sound_id: string;
  url: string;
  waveform_base_64?: string;
}

interface SimpleSoundResponse extends SoundResponse {}

export interface GetSoundboardResponse extends Omit<CreateSoundboardRequest, "sounds"> {
  created_at_unix: number;
  created_by_user_id: string;
  public: boolean;
  soundboard_id: string;
  sounds: SoundResponse[];
}

export interface GetSoundboardsResponse extends Omit<GetSoundboardResponse, "sounds"> {
  sounds: SimpleSoundResponse[];
}

/**
 * Creates a new soundboard preset
 * @param data The soundboard data to create
 * @returns The created soundboard
 */
export async function createSoundboard(
  data: CreateSoundboardRequest,
  authToken: string,
): Promise<GetSoundboardResponse> {
  const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/soundboards`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create soundboard: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Retrieves a soundboard by ID
 * @param soundboardId The ID of the soundboard to retrieve
 * @returns The soundboard data
 */
export async function getSoundboard(soundboardId: string, authToken?: string): Promise<GetSoundboardResponse> {
  const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/soundboards/${soundboardId}`, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch soundboard: ${response.statusText}`);
  }

  const res = (await response.json()) as GetSoundboardResponse;
  const soundIdToIdx = {} as Record<string, number>;
  const soundIdsToFetch = [] as string[];

  res.sounds = res.sounds.map((s, idx) => {
    if (s.waveform_base_64) {
      return {
        ...s,
        url: `data:audio/wav;base64,${s.waveform_base_64}`,
      };
    }
    if (s.default_sound_id && s.default_sound_id in DEFAULT_SOUND_URLS) {
      return {
        ...s,
        url: DEFAULT_SOUND_URLS[s.default_sound_id],
      };
    }
    if (s.default_sound_id) {
      soundIdsToFetch.push(s.default_sound_id);
      soundIdToIdx[s.default_sound_id] = idx;
    }
    return s;
  });

  try {
    const idsKey = soundIdsToFetch.join(",");
    const data = await fetchWithCache<{ sounds: { docs: SoundEffect[] } }>(
      `${env.NEXT_PUBLIC_SERVER_URL}/next/api/sounds?ids=${idsKey}`,
      { method: "GET" },
      {
        keys: ["soundboard-sounds", idsKey],
        tags: ["soundboard-sounds"],
        revalidate: CACHE_TAG_REVALIDATE["soundboard-sounds"],
      },
    );
    (data.sounds.docs as SoundEffect[]).forEach((s, idx) => {
      const url = (s.url || (s.media as Media).url) as string;
      res.sounds[soundIdToIdx[soundIdsToFetch[idx]]].url = url;
    });
  } catch (e) {
    logger.error(e instanceof Error ? e : null, "Failed to fetch sounds from Payload");
  }
  return res;
}

/**
 * Retrieves all soundboards for the current user
 * @returns The soundboard data
 */
export async function listMySoundboards(authToken: string): Promise<GetSoundboardResponse[]> {
  const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/soundboards`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch soundboard: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Updates a soundboard by ID
 * @param data The soundboard data to update
 * @returns The updated soundboard
 */
export async function updateSoundboard(
  soundboardId: string,
  data: Partial<UpdateSoundboardRequest>,
  authToken: string,
): Promise<GetSoundboardResponse> {
  const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/soundboards/${soundboardId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to update soundboard: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Deletes a soundboard by ID
 * @param soundboardId The ID of the soundboard to delete
 * @returns The deleted soundboard
 */
export async function deleteSoundboard(soundboardId: string, authToken: string): Promise<void> {
  const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/soundboards/${soundboardId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete soundboard: ${response.statusText}`);
  }
}
